import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import moment from 'moment';

import CheckBox from '../atoms/form/CheckBox';
import SelectBox from '../atoms/form/SelectBox';
import LineTitle from '../atoms/titles/LineTitle';
import LinkButton from '../atoms/button/LinkButton';
import {
  setStayNights,
  setNumberOfRooms,
  setPeoplePerRoom,
  setRoomType,
  setMealCondition,
  setTagId,
  setDirectionId,
  setPrefectureId,
  setAreaId,
  setDistrictId,
  setKeyword,
} from '../../modules/hotel';
import { createOptions, convertEnumValuesToOptions, addUnselectedOption } from '../../utils/search';
import { FetchRoomTypes, FetchMealConditions, FetchTaggroups, FetchDirections } from '../../queries/index.graphql';
import { useRoomAllocation } from '../../utils/roomAllocation';
import { DEFAULT_UNSELECETD_LABEL, ENABLE_CHANGE_STAY_NIGHTS_FEATURE, UNSELECTED_LABEL } from '../../constants';

const selector = (state) => ({
  hotel: state.hotel,
  flight: state.flight,
});

const SearchBoxFacility = ({
  hideDestination,
  hideRoom,
  hideStaynights,
  hideTitle,
  limitedOptions,
  handleSearch,
  maxStayNights,
}) => {
  const [ isHide, setIsHide ] = useState(true);
  const { hotel, flight } = useSelector(selector);
  const { hotelsInput, queryParamsValidated: queryParamsValidatedHotel } = hotel;
  const {
    stayNights,
    numberOfRooms,
    peoplePerRoom,
    directionId,
    prefectureId,
    areaId,
    districtId,
    roomType,
    mealCondition,
    tagIds,
    keyword,
    // sortOrder,
  } = hotelsInput;

  const dispatch = useDispatch();

  const { data: roomTypesData } = useQuery(FetchRoomTypes);
  const roomTypes = roomTypesData ? convertEnumValuesToOptions(roomTypesData.__type.enumValues) : [];
  const { data: mealConditionsData } = useQuery(FetchMealConditions);
  const mealConditions = mealConditionsData ? convertEnumValuesToOptions(mealConditionsData.__type.enumValues) : [];
  const { data: taggroupsData } = useQuery(FetchTaggroups);
  let taggroups =  taggroupsData && taggroupsData.taggroups;
  if (taggroups && limitedOptions) {
    taggroups = taggroups.filter((tg) => tg.id !== '1');
  }

  const { data } = useQuery(FetchDirections);
  const directions = data ? data.directions : [];
  const direction = directions.find((d) => d.id === directionId);
  const prefectures = direction ? direction.prefectures : [];
  const prefecturesOptions = addUnselectedOption(prefectures, UNSELECTED_LABEL);
  const prefecture = prefectures.find((p) => p.id && p.id === prefectureId);
  const areas = prefecture ? addUnselectedOption(prefecture.areas, UNSELECTED_LABEL) : addUnselectedOption([]);
  const area = areas.find((a) => a.id && a.id === areaId);
  const districts = area ? addUnselectedOption(area.districts, UNSELECTED_LABEL) : addUnselectedOption([]);

  useEffect(() => {
    if (maxStayNights) {
      dispatch(
        setStayNights(maxStayNights)
      );
    }
  }, [maxStayNights]);

  const handleChangeStayNights = useCallback((e) => {
    dispatch(
      setStayNights(Number(e.target.value))
    );
  }, []);

  const handleChangeNumberOfRooms = useCallback((e) => {
    dispatch(
      setNumberOfRooms(Number(e.target.value))
    );
  }, []);

  const handleChangeRoomType = useCallback((e) => {
    const value = e.target.value !== DEFAULT_UNSELECETD_LABEL ? e.target.value : undefined;
    dispatch(
      setRoomType(value)
    );
  }, []);

  const handleChangeMealCondition = useCallback((e) => {
    dispatch(
      setMealCondition(e.target.value)
    );
  }, []);

  const handleChangeKeyword = useCallback((e) => {
    dispatch(
      setKeyword(e.target.value)
    );
  }, []);

  const handleChangeDirection = useCallback((e) => {
    const newDirectionId = e.target.value;
    // const newDirection = directions.find((d) => d.id === newDirectionId);
    // const newPrefectureId = newDirection.prefectures ? newDirection.prefectures[0].id : undefined;
    const newPrefectureId = undefined;
    dispatch(
      setDirectionId(newDirectionId)
    );
    dispatch(
      setPrefectureId(newPrefectureId)
    );
    dispatch(
      setAreaId(undefined)
    );
    dispatch(
      setDistrictId(undefined)
    );
  }, [directions]);

  const handleChangePrefecture = useCallback((e) => {
    const value = e.target.value !== UNSELECTED_LABEL ? e.target.value : undefined;
    dispatch(
      setPrefectureId(value)
    );
    dispatch(
      setAreaId(undefined)
    );
    dispatch(
      setDistrictId(undefined)
    );
  }, []);

  const handleChangeArea = useCallback((e) => {
    const value = e.target.value !== UNSELECTED_LABEL ? e.target.value : undefined;
    dispatch(
      setAreaId(value)
    );
    dispatch(
      setDistrictId(undefined)
    );
  }, []);

  const handleChangeDistrict = useCallback((e) => {
    const value = e.target.value !== UNSELECTED_LABEL ? e.target.value : undefined;
    dispatch(
      setDistrictId(value)
    );
  }, []);

  const { flightsInput, queryParamsValidated: queryParamsValidatedFlight } = flight;
  const departureDate = moment(flightsInput.outbound.date).format('YYYY年MM月DD日(ddd)');
  const arrivalDate = moment(flightsInput.inbound.date).format('YYYY年MM月DD日(ddd)');
  const period = `${departureDate} 〜 ${arrivalDate}`;

  const { numberOfRoomsOptions, peoplePerRoom: calculatedPeoplePerRoom } = useRoomAllocation(flightsInput, numberOfRooms);

  useEffect(() => {
    if (!(queryParamsValidatedFlight && queryParamsValidatedHotel)) {
      return;
    }

    const validNumberOfRooms = numberOfRoomsOptions.find((option) => option.value === numberOfRooms);
    if (!validNumberOfRooms && numberOfRoomsOptions && numberOfRoomsOptions.length) {
      dispatch(
        setNumberOfRooms(Number(numberOfRoomsOptions[0].value))
      );
    }
  }, [numberOfRoomsOptions, numberOfRooms, queryParamsValidatedFlight, queryParamsValidatedHotel]);

  useEffect(() => {
    if (!(queryParamsValidatedFlight && queryParamsValidatedHotel)) {
      return;
    }

    if (peoplePerRoom !== calculatedPeoplePerRoom) {
      dispatch(
        setPeoplePerRoom(Number(calculatedPeoplePerRoom))
      );
    }
  }, [calculatedPeoplePerRoom, queryParamsValidatedFlight, queryParamsValidatedHotel]);

  const stayNightsOptions = useMemo(() => createOptions(1, maxStayNights || 7, '泊'), [maxStayNights]);

  return (
    <div className="SearchBoxFacilityContainer">
      <div className="searchItem">
        {!hideTitle && (
          <LineTitle text={'宿泊条件'}>
            <span style={{ marginLeft: 20 }}>{period}</span>
          </LineTitle>
        )}
        {!hideStaynights && (
          <div className="formBlock lodging" style={{ marginTop: hideTitle ? 0 : undefined }}>
            <div className="formTitle">泊数</div>
            <div className="formItems">
              <div className="formItem">
                {ENABLE_CHANGE_STAY_NIGHTS_FEATURE ? (
                  <SelectBox
                    size="sm"
                    className="formTextSpaceRight"
                    options={stayNightsOptions}
                    value={stayNights}
                    onChange={handleChangeStayNights}
                  />
                ) : (
                  <div style={{ fontWeight: 'bold' }}>{stayNights}泊</div>
                )}
              </div>
            </div>
          </div>
        )}
        {!hideRoom && (
          <div className="formBlock lodging">
            <div className="formTitle">部屋数</div>
            <div className="formItems">
              <div className="formItem formItemRoom">
                <SelectBox
                  size="sm"
                  className="formTextSpaceRight"
                  options={numberOfRoomsOptions}
                  value={numberOfRooms}
                  onChange={handleChangeNumberOfRooms}
                />
              </div>
              <div className="formItem formItemRoom">
                <div style={{ width: 90 }}>利用人数</div>
                <div style={{ fontWeight: 'bold' }}>{peoplePerRoom}名</div>
              </div>
            </div>
          </div>
        )}
        {!hideDestination && (
          <div className="formBlock lodging">
            <div className="formTitle">目的地</div>
            <div className="formItems">
              <div className="formItem formItemArea">
                方面
                <SelectBox
                  className="formTextSpaceLeft"
                  options={directions}
                  value={directionId}
                  onChange={handleChangeDirection}
                />
              </div>
              <div className="formItem formItemArea">
                都道府県
                <SelectBox
                  className="formTextSpaceLeft"
                  options={prefecturesOptions}
                  value={prefectureId}
                  onChange={handleChangePrefecture}
                />
              </div>
              <div className="formItem formItemArea">
                エリア
                <SelectBox
                  className="formTextSpaceLeft"
                  options={areas}
                  value={areaId}
                  onChange={handleChangeArea}
                />
              </div>
              <div className="formItem formItemArea">
                地区
                <SelectBox
                  className="formTextSpaceLeft"
                  options={districts}
                  value={districtId}
                  onChange={handleChangeDistrict}
                />
              </div>
            </div>
          </div>
        )}
        <div className="formBlock lodging">
          <div className="formTitle">部屋タイプ</div>
          <div className="formItems">
            <SelectBox
              options={roomTypes}
              value={roomType}
              onChange={handleChangeRoomType}
              size="lg"
            />
          </div>
        </div>
        <div className="formBlock lodging">
          <div className="formTitle">食事条件</div>
          <div className="formItems">
            <SelectBox
              options={mealConditions}
              value={mealCondition}
              onChange={handleChangeMealCondition}
            />
          </div>
        </div>
        <div className="formBlock lodging">
          <div className="formTitle">キーワード</div>
          <div className="formItems wide">
            <input
              className="input light"
              value={keyword}
              onChange={handleChangeKeyword}
              placeholder="キーワード・施設名など"
            />
          </div>
        </div>
      </div>
      {!isHide && (
        <div className="searchItem kodawari">
          <LineTitle text="こだわり条件" />
          <div className="formBlock">
            {taggroups && taggroups.map(({ id, name, tags }) => (
              <div key={id} className="formItemsCheckbox">
                <div className="formTitle">{name}</div>
                <div className="checkboxes">
                  {tags.map(({ id: tagId, name }) => (
                    <div key={tagId} className="formItemCheckBox">
                      <CheckBox
                        name={`searchBoxHotel-${tagId}`}
                        id={`searchBoxHotel-${tagId}`}
                        checked={tagIds.includes(tagId)}
                        onChange={() => dispatch(setTagId(tagId))}
                      >
                        {name}
                      </CheckBox>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="actionWrapper">
        <div className="action" onClick={() => setIsHide(!isHide)}>
          {isHide ? (
            <div className="open">こだわり条件を追加する</div>
          ) : (
            <div className="close">閉じる</div>
          )}
        </div>
      </div>
      <div className="searchButton">
        <LinkButton onClick={handleSearch}>再検索する</LinkButton>
      </div>
    </div>
  );
};

SearchBoxFacility.propTypes = {
  hideDestination: PropTypes.bool,
  hideRoom: PropTypes.bool,
  hideStaynights: PropTypes.bool,
  hideTitle: PropTypes.bool,
  limitedOptions: PropTypes.bool,
  handleSearch: PropTypes.func.isRequired,
  maxStayNights: PropTypes.number,
};

export default SearchBoxFacility;
